import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { FileStack, Pencil, Trash2, Check, X, Sparkles, Brain } from "lucide-react";
import { toast } from "sonner";

export function ImportTemplatesManager({ canEdit }) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");

  const load = () => api.get("/esg-import/templates").then((r) => setTemplates(r.data.templates)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    if (!label.trim()) return;
    try { await api.patch(`/esg-import/templates/${id}`, { label: label.trim() }); setEditing(null); load(); toast.success("Template renamed"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Clear this remembered file format and its learned mappings? Future uploads of this format will be re-analyzed by AI.")) return;
    try { await api.delete(`/esg-import/templates/${id}`); load(); toast.success("Template cleared"); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="import-templates-manager">
      <p className="text-sm text-muted-foreground">Remembered file formats from <span className="text-foreground">AI ESG Bulk Import</span>. Each time you commit an import, Lestari learns the file's columns and line-item classifications so recurring uploads (e.g. a monthly ERP export) are classified instantly. {canEdit ? "Rename for clarity, or clear one to retrain the AI when an export's columns change." : "Read-only."}</p>

      {templates.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm" data-testid="no-templates">
          <FileStack className="h-8 w-8 mx-auto mb-2 opacity-50" />
          No saved formats yet. Import a CSV/Excel via Data Management → ESG Records → AI Import, and Lestari will remember its layout here.
        </div>
      ) : templates.map((t) => (
        <div key={t.id} className="card-flat p-4" data-testid={`template-${t.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {editing === t.id ? (
                <div className="flex items-center gap-1.5">
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 max-w-xs" onKeyDown={(e) => e.key === "Enter" && save(t.id)} data-testid={`template-label-input-${t.id}`} />
                  <Button size="icon" variant="primary" className="h-8 w-8" onClick={() => save(t.id)} data-testid={`template-save-${t.id}`}><Check className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ) : (
                <div className="font-display font-semibold flex items-center gap-2"><Brain className="h-4 w-4 text-accent" /> {t.label}</div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="success" className="text-[10px] inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> {t.learned_count} learned items</Badge>
                <Badge variant="outline" className="text-[10px]">Used {t.use_count}×</Badge>
                {t.updated_at && <Badge variant="outline" className="text-[10px]">Updated {String(t.updated_at).slice(0, 10)}</Badge>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">
                <span className="uppercase tracking-[0.1em]">Columns:</span> {(t.headers || []).join(", ") || "—"}
              </div>
              {t.learned_samples?.length > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1 truncate"><span className="uppercase tracking-[0.1em]">Learned:</span> {t.learned_samples.join(" · ")}{t.learned_count > t.learned_samples.length ? " …" : ""}</div>
              )}
            </div>
            {canEdit && editing !== t.id && (
              <div className="flex items-center gap-0.5 shrink-0">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(t.id); setLabel(t.label); }} data-testid={`template-rename-${t.id}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
